import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material'
import { BaseDialogProps } from './BaseDialogProps'
import SelectInput from '../Form/Inputs/SelectInput'
import { t } from 'i18next'
import AutocompleteInput from '../Form/Inputs/AutocompleteInput'
import sortByField from '../../store/utils/sortByField'
import DateInput from '../Form/Inputs/DateInput'
import dayjs from 'dayjs'
import TextInput from '../Form/Inputs/TextInput'
import React, { useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { defaultRecruitmentFormValues, RecruitmentFormValues } from '../../store/types/form/RecruitmentFormValues'
import ConfirmButton from '../Button/ConfirmButton'
import { Trans } from 'react-i18next'
import { KanbanColumn } from '../../views/CandidatesView/KanbanBoard/CandidatesKanbanBoard'
import DateFormat from '../../store/types/enums/DateFormat'
import { createErrorMessage } from '../../store/utils'
import { useCreateRecruitmentMutation } from '../../store/redux/reducers/recruitmentSlice'
import { useNotification } from '../../store/redux/hooks'
import { useRecruitmentWorkflow } from '../../store/utils/useRecruitmentWorkflow'
import { TimePicker } from '@mui/x-date-pickers'
import { Box } from '@mui/system'
import DecisionEnum from '../../store/types/enums/DecisionEnum'

type CandidateKanbanFormDialogProps = BaseDialogProps & {
  stage: KanbanColumn | undefined
  candidateId: string | undefined
  fullName: string
  setOpenDialog: React.Dispatch<React.SetStateAction<boolean>>
  decision: DecisionEnum | undefined
}

const CandidateKanbanFormDialog = ({
  open,
  onClose,
  stage,
  candidateId,
  fullName,
  setOpenDialog,
  decision,
}: CandidateKanbanFormDialogProps) => {
  const { displayNotification } = useNotification()
  const isRejected = useMemo(() => {
    return decision === DecisionEnum.REJECTED
  }, [decision])

  const { control, handleSubmit, reset, formState, setValue, watch } = useForm<RecruitmentFormValues>({
    defaultValues: async () => {
      if (stage?.id) {
        return { ...defaultRecruitmentFormValues, stage: stage.id }
      } else return defaultRecruitmentFormValues
    },
  })

  const watchedStage = watch('stage')
  const { managementAndRecruiters, stageOptions, stepOptions, decisionOptions } = useRecruitmentWorkflow(watchedStage)

  const [createRecruitment] = useCreateRecruitmentMutation()

  useEffect(() => {
    if (stage?.id && formState.defaultValues?.stage === '') {
      setValue('stage', stage.id)
    }
  }, [stage, formState.defaultValues, setValue])

  useEffect(() => {
    if (isRejected) setValue('decision', DecisionEnum.REJECTED)
  }, [isRejected, setValue])

  useEffect(() => {
    if (stepOptions.length === 1) {
      const value = stepOptions.at(0)?.value
      if (value) {
        setValue('step', value)
      }
    }
  }, [stepOptions, setValue])

  const handleSubmitCreate = () => {
    handleSubmit(data => handleCreate(data))()
  }

  const handleCreate = async (data: RecruitmentFormValues) => {
    const mergedCompletionDate =
      data.completionDate && data.completionTime
        ? data.completionDate
            .set('hour', data.completionTime.hour())
            .set('minute', data.completionTime.minute())
            .set('second', 0)
        : undefined

    const dataWithCandidateId = {
      ...data,
      candidateId: candidateId,
      completionDate: mergedCompletionDate?.format(DateFormat.IsoLocalDateTime),
      responsibles: data.responsibles?.map(responsible => responsible.value),
      completionTime: undefined,
    }
    try {
      await createRecruitment(dataWithCandidateId).unwrap()
      displayNotification({ message: t('page.addRecruitment.alert.recruitmentCreated'), type: 'success' })
      reset()
      setOpenDialog(false)
    } catch (error) {
      let errorDescription = createErrorMessage(error)
      displayNotification({
        message: `${t('page.addRecruitment.alert.recruitmentCreationError')} ${errorDescription}`,
        type: 'error',
      })
    }
  }

  const handleClose = () => {
    if (onClose) {
      onClose()
      reset(defaultRecruitmentFormValues)
    }
  }

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      PaperProps={{
        sx: {
          paddingLeft: 1,
          paddingRight: 2,
          paddingTop: 1,
          paddingBottom: 2,
        },
      }}
    >
      <DialogTitle>
        <Trans i18nKey="page.candidates.dialog.kanbanChange" values={{ name: fullName }} />
      </DialogTitle>
      <DialogContent>
        <Stack sx={{ gap: 3, width: '100%', py: 1, minWidth: 350 }}>
          <SelectInput
            disabled={isRejected}
            options={stageOptions}
            name={'stage'}
            control={control}
            label={t('page.addRecruitment.stage') + ' *'}
            rules={{ required: t('page.addRecruitment.errors.stageRequired') }}
          />

          <SelectInput
            options={stepOptions}
            name={'step'}
            control={control}
            label={t('page.addRecruitment.step') + ' *'}
            rules={{ required: t('page.addRecruitment.errors.stepRequired') }}
          />

          <AutocompleteInput
            options={
              managementAndRecruiters
                ? managementAndRecruiters.content.map(user => ({
                    value: user.id,
                    label: `${user.firstName} ${user.lastName}`,
                  }))
                : []
            }
            multiple
            disableCloseOnSelect
            size={'small'}
            name={'responsibles'}
            control={control}
            label={t('page.candidateDetails.tiles.responsibles')}
            sortValues={values => sortByField(values, 'label')}
          />
          <SelectInput
            disabled={isRejected}
            options={decisionOptions}
            name={'decision'}
            control={control}
            label={t('page.addRecruitment.decision') + ' *'}
            rules={{ required: t('page.addRecruitment.errors.decisionRequired') }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
            <DateInput
              control={control}
              name={'completionDate'}
              label={t('page.addRecruitment.completionDate') + ' *'}
              defaultValue={dayjs().utc(true)}
              rules={{ required: t('page.addRecruitment.errors.completionDateRequired') }}
            />
            <Controller
              name={'completionTime'}
              control={control}
              rules={{ required: t('page.addRecruitment.errors.completionTimeRequired') }}
              render={({ field: { value, onChange, name }, fieldState }) => (
                <TimePicker
                  value={value || null}
                  label={t('page.addRecruitment.completionTime') + ' *'}
                  onChange={onChange}
                  ampm={false}
                  slotProps={{
                    textField: {
                      size: 'small',
                      helperText: fieldState.error?.message,
                      error: !!fieldState.error || !dayjs(value, DateFormat.HourMinute, true).isValid(),
                      name: name,
                    },
                  }}
                />
              )}
            />
          </Box>
          <TextInput
            name={'comments'}
            control={control}
            label={t('page.addRecruitment.comments')}
            maxLength={500}
            rules={{
              maxLength: { value: 500, message: t('page.worklog.form.descriptionLengthError') },
            }}
            multiline
            rows={7}
            showCharCounter
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ gap: 2 }}>
        <Button onClick={handleClose} size="small">
          {t('page.schedule.confirmCopyDialog.return')}
        </Button>
        <ConfirmButton
          text={t('page.schedule.confirmCopyDialog.confirm')}
          onClick={handleSubmitCreate}
          isDisabled={false}
        />
      </DialogActions>
    </Dialog>
  )
}

export default CandidateKanbanFormDialog